import { CurrencyExchangeService } from './../../../core/services/currency-exchange.service';
import { CurrencyOrderService } from '../../../core/services/currency-order.service';
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TrackedDialogService } from '../../../core/services/tracked-dialog.service';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { CurrencyExchange } from '../../../core/models/currency-exchange ';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';
import { AddRateExchangeComponent } from './add-currency-form/add-currency-form.component';
import { forkJoin } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';

@Component({
  selector: 'em-currency-exchange',
  standalone: true,
  imports: [CommonModule, TableModule, ButtonModule, ToastModule, ConfirmDialogModule, TranslatePipe, EmPermissionDirective, FormsModule, CalendarModule],
  templateUrl: './currency-exchange.component.html',
  styleUrl: './currency-exchange.component.scss',
  providers: [MessageService, ConfirmationService, TrackedDialogService],
})
export class CurrencyExchangeComponent {
  pivotedRows: any[] = [];
  displayedCurrencies: string[] = [];
  activeCurrencies: string[] = [];
  currencies!: CurrencyExchange[];
  ref: DynamicDialogRef | undefined;
  translations: any = {};
  Permission = Permission;
  filterStartDate: Date | null = null;
  filterEndDate: Date | null = null;
  allPivotedRows: any[] = []; // keeps unfiltered rows
  constructor(
    private currencyExchangeService: CurrencyExchangeService,
    private currencyOrderService: CurrencyOrderService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService,
  ) { }

  ngOnInit() {
    this.getAllCurrencies();
    this.loadTranslations();
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.currencies.title',
        'layout.configurations.currencies.currency',
        'layout.configurations.currencies.exchangeRate',
        'layout.configurations.currencies.newRateExchange',
        'layout.configurations.currencies.actions',
        'layout.configurations.currencies.addCurrency.title',
        'layout.configurations.currencies.addCurrency.successMessage.title',
        'layout.configurations.currencies.addCurrency.successMessage.detail',
        'layout.configurations.currencies.addCurrency.errorMessage.title',
        'layout.configurations.currencies.addCurrency.errorMessage.alreadyExists',
        'layout.configurations.currencies.addCurrency.errorMessage.errorSavingCurrency',
        'layout.configurations.currencies.editCurrency.title',
        'layout.configurations.currencies.editCurrency.successMessage.title',
        'layout.configurations.currencies.editCurrency.successMessage.detail',
        'layout.configurations.currencies.editCurrency.errorMessage.title',
        'layout.configurations.currencies.editCurrency.errorMessage.alreadyExists',
        'layout.configurations.currencies.editCurrency.errorMessage.errorUpdatingCurrency',
        'layout.configurations.currencies.deleteCurrency.title',
        'layout.configurations.currencies.deleteCurrency.detail',
        'layout.configurations.currencies.deleteCurrency.yes',
        'layout.configurations.currencies.deleteCurrency.no',
        'layout.configurations.currencies.deleteCurrency.successMessage.title',
        'layout.configurations.currencies.deleteCurrency.successMessage.detail',
        'layout.configurations.currencies.deleteCurrency.errorMessage.title',
        'layout.configurations.currencies.deleteCurrency.errorMessage.detail',
        'layout.configurations.currencies.deleteCurrency.warningMessage.title',
        'layout.configurations.currencies.deleteCurrency.warningMessage.detail',
        'layout.configurations.currencies.deleterowrate.title',
        'layout.configurations.currencies.deleterowrate.detail',
        'layout.configurations.currencies.deleterowrate.yes',
        'layout.configurations.currencies.deleterowrate.no',
        'layout.configurations.currencies.addrateexchange.successMessage.title',
        'layout.configurations.currencies.addrateexchange.successMessage.detail'
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

getAllCurrencies() {
  forkJoin({
      exchangeRates: this.currencyExchangeService.getCurrencies(),
      activeCurrencies: this.currencyExchangeService.getActiveCurrencies()
    }).subscribe(({ exchangeRates, activeCurrencies }) => {
      this.currencies = exchangeRates;

    // Active currencies drive creation and the main column order.
    const activeCurrencyNames = activeCurrencies
      .map((c: any) => (c.currencyName as string).toUpperCase());
    this.activeCurrencies = this.currencyOrderService.applyOrder(activeCurrencyNames);

    // Keep currencies that already have historical rates visible after they are deactivated.
    const historicalCurrencyNames = [...new Set(
      exchangeRates
        .filter(rate => rate.exchangeRate !== null && rate.currencyName)
        .map(rate => rate.currencyName!.toUpperCase())
    )];
    const inactiveHistoricalCurrencies = historicalCurrencyNames
      .filter(currency => !this.activeCurrencies.includes(currency));
    this.displayedCurrencies = [...this.activeCurrencies, ...inactiveHistoricalCurrencies];

    this.buildPivotTable(exchangeRates);
  });
}

addCurrencyToTable(currencyCode: string) {
  currencyCode = currencyCode.toUpperCase();

  if (this.displayedCurrencies.includes(currencyCode)) {
    return;
  }

  this.displayedCurrencies = [...this.displayedCurrencies, currencyCode];

  this.pivotedRows = this.pivotedRows.map(row => ({
    ...row,
    [currencyCode]: null
  }));

  if (this.pivotedRows.length === 0) {
    this.pivotedRows = [{
      startdate: null,
      enddate: null,
      [currencyCode]: null
    }];
  }
}
onAddRateExchange() {
  this.ref = this.dialogService.open(AddRateExchangeComponent, {
    header: this.translations['layout.configurations.currencies.newRateExchange'],
    width: '600px',
    data: {
      mode: 'add',
      currencies: this.activeCurrencies
    }
  });

this.ref.onClose.subscribe(result => {
  if (!result) return;

  this.insertNewRow(result);

  this.messageService.add({
    severity: 'success',
    summary: this.translations['layout.configurations.currencies.addrateexchange.successMessage.title'],
    detail: this.translations['layout.configurations.currencies.addrateexchange.successMessage.detail'],
  });
});
}

  private minusOneDay(date: Date): Date {
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    return d;
  }

  private formatDateLocal(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

insertNewRow(data: any) {
  const newRow: any = {
    startdate: data.startDate,
    enddate: data.endDate ?? null
  };

  data.rates.forEach((r: any) => {
    newRow[r.currency] = r.exchangeRate;
  });

  if (this.pivotedRows.length > 0) {
    const firstRow = this.pivotedRows[0];
    const newEndDate = this.minusOneDay(new Date(data.startDate));
    const newEndDateStr = this.formatDateLocal(newEndDate);
    const updatePayload = {
      oldStartDate: firstRow.startdate,
      oldEndDate: firstRow.enddate ?? null,
      startDate: firstRow.startdate,
      endDate: newEndDateStr,
      rates: this.displayedCurrencies.map((c: string) => ({
        currencyName: c,
        exchangeRate: firstRow[c] ?? null
      }))
    };
     firstRow.enddate = newEndDateStr;
    this.currencyExchangeService.updateRow(updatePayload).subscribe({
      next: () => {
      },
      error: (err: any) => {
        console.error('Failed to update previous row end date:', err);
        firstRow.enddate = null;
      }
    });
  }

  this.pivotedRows = [newRow, ...this.pivotedRows];
}

onEdit(row: any) {
  const currenciesForEdit = [
    ...this.activeCurrencies,
    ...this.displayedCurrencies.filter(c =>
      !this.activeCurrencies.includes(c) && row[c] !== null && row[c] !== undefined
    )
  ];
  const existingRates = currenciesForEdit.map(c => ({
    currency: c,
    exchangeRate: row[c] ?? null
  }));

  this.ref = this.dialogService.open(AddRateExchangeComponent, {
    header: this.translations['layout.configurations.currencies.editCurrency.title'],
    width: '600px',
    contentStyle: { overflow: 'auto' },
    data: {
      mode: 'edit',
      startDate: row.startdate ? new Date(row.startdate) : null,
      endDate: row.enddate ? new Date(row.enddate) : null,
      currencies: currenciesForEdit,
      rates: existingRates
    }
  });

  this.ref.onClose.subscribe(result => {
    if (result) {
      this.updateRow(row, result);
    }
  });
}
updateRow(row: any, data: any) {

  const payload = {
    oldStartDate: row.startdate,
    oldEndDate: row.enddate ?? null,
    startDate: data.startDate
      ? this.formatDateLocal(data.startDate)
      : row.startdate,
    endDate: data.endDate
      ? this.formatDateLocal(data.endDate)
      : null,
    rates: data.rates.map((r: any) => ({
      currencyName: r.currency,
      exchangeRate: r.exchangeRate
    }))
  };

  this.currencyExchangeService.updateRow(payload).subscribe({
    next: () => {
      row.startdate = data.startDate ?? row.startdate;
      row.enddate = data.endDate ?? null;
      data.rates.forEach((r: any) => {
        row[r.currency] = r.exchangeRate;
      });
      this.messageService.add({
        severity: 'success',
        summary: this.translations['layout.configurations.currencies.editCurrency.successMessage.title'],
        detail: this.translations['layout.configurations.currencies.editCurrency.successMessage.detail'],
      });
    },
    error: (err) => {
      this.messageService.add({
        severity: 'error',
        summary: this.translations['layout.configurations.currencies.editCurrency.errorMessage.title'],
        detail: (typeof err?.error === 'string' ? err.error : err?.error?.message) || this.translations['layout.configurations.currencies.editCurrency.errorMessage.errorUpdatingCurrency'],
        life: 5000
      });
    }
  });
}
  onDelete(event: Event, row: any) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translations['layout.configurations.currencies.deleterowrate.detail'],
      icon: 'pi pi-info-circle',
      header: this.translations['layout.configurations.currencies.deleterowrate.title'],
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translations['layout.configurations.currencies.deleterowrate.yes'],
      rejectLabel: this.translations['layout.configurations.currencies.deleterowrate.no'],
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.currencyExchangeService
        .deleteRow(row.startdate, row.enddate)
         .subscribe(() => {
    this.pivotedRows = this.pivotedRows.filter(
      r => r.startdate !== row.startdate
    );
  });
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translations['layout.configurations.currencies.deleteCurrency.warningMessage.title'],
          detail: this.translations['layout.configurations.currencies.deleteCurrency.warningMessage.detail'],
          life: 3000,
        });
      },
    });
  }
buildPivotTable(data: CurrencyExchange[]) {
  if (!data || data.length === 0) {
    this.pivotedRows = [];
    return;
  }
  data = [...data].sort((a, b) => {
    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
  });
  const rowsMap = new Map<string, any>();

  data
    .filter(item => item.exchangeRate !== null)
    .forEach(item => {
      const key = `${item.startDate}_${item.endDate ?? 'ACTIVE'}`;
      if (!rowsMap.has(key)) {
        rowsMap.set(key, {
          id: item.id,
          startdate: item.startDate,
          enddate: item.endDate ?? null
        });
      }
      rowsMap.get(key)![item.currencyName!.toUpperCase()] = item.exchangeRate;
    });

  this.pivotedRows = Array.from(rowsMap.values()).sort((a, b) => {
     return new Date(b.startdate).getTime() - new Date(a.startdate).getTime();
  });
  this.allPivotedRows = [...this.pivotedRows];
}
applyDateFilter(): void {
  if (!this.filterStartDate && !this.filterEndDate) {
    this.pivotedRows = [...this.allPivotedRows];
    return;
  }
  this.pivotedRows = this.allPivotedRows.filter(row => {
    const rowStart = row.startdate ? new Date(row.startdate) : null;
    const rowEnd   = row.enddate   ? new Date(row.enddate)   : null;
    // Filter: keep rows whose date range overlaps the selected filter range
    const afterStart = !this.filterStartDate || (rowEnd === null || rowEnd >= this.filterStartDate!);
    const beforeEnd  = !this.filterEndDate   || (rowStart === null || rowStart <= this.filterEndDate!);
    return afterStart && beforeEnd;
  });
}
clearDateFilter(): void {
  this.filterStartDate = null;
  this.filterEndDate = null;
  this.pivotedRows = [...this.allPivotedRows];
}
}
